import type { PredictionMarketSnapshot } from "@roman/shared/types/PredictionMarket";
import { addMinutes, format, setMilliseconds, setMinutes, setSeconds, subDays } from "date-fns";
import type { ChartHistoryFilterType } from "./constants";
import { SAMPLING_INTERVAL_MINUTES } from "./constants";

export type ChartDataPoint = {
  time: string;
  [choiceId: string]: string;
};

/**
 * Bucketizes historical data at specified time intervals and records probabilities for each choice.
 *
 * @param history - Sorted prediction market snapshot history in ascending order
 * @param filterType - Display range filter ("all", "1M", "1W", "1D")
 * @param now - Reference time (defaults to current time)
 * @returns Array of data containing timestamps and probabilities for each choice per bucket
 *
 * @remarks
 * This implementation assumes the historical data is sorted and performs bucket processing in O(n) time.
 */
export const bucketChoiceProbabilities = (
  history: PredictionMarketSnapshot[],
  filterType: ChartHistoryFilterType,
  now: number = Date.now(),
): ChartDataPoint[] => {
  if (history.length === 0) return [];

  const intervalMinutes = SAMPLING_INTERVAL_MINUTES[filterType];
  const cutOffDays = toDays(filterType);

  const endTime = now;
  const startTime =
    cutOffDays !== null
      ? Math.max(subDays(new Date(now), cutOffDays).getTime(), history[0].timeStamp)
      : history[0].timeStamp;

  const result: ChartDataPoint[] = [];
  const usedTimestamps = new Set<string>();
  let currentTimeIncluded = false;

  let historyIndex = 0;
  let samplingTime = startTime;

  // Find the starting index for our time range
  while (historyIndex < history.length && history[historyIndex].timeStamp < startTime) {
    historyIndex++;
  }

  // Reset to 0 if all data is after startTime
  if (historyIndex >= history.length) {
    historyIndex = 0;
  }

  while (samplingTime <= endTime) {
    // Advance index to the latest snapshot that occurs before or at the sampling time
    while (historyIndex + 1 < history.length && history[historyIndex + 1].timeStamp <= samplingTime) {
      historyIndex++;
    }

    // Find the nearest snapshot for the current sampling time point
    let nearestSnapshot: PredictionMarketSnapshot | null = null;

    if (historyIndex < history.length) {
      // Check if we have data before and after the sampling time
      if (historyIndex > 0 && history[historyIndex].timeStamp > samplingTime) {
        // Use the previous snapshot if current one is in the future
        nearestSnapshot = history[historyIndex - 1];
      } else if (history[historyIndex].timeStamp <= samplingTime) {
        nearestSnapshot = history[historyIndex];
      }
    }

    if (nearestSnapshot) {
      const formattedTime = formatTime(samplingTime);
      if (!usedTimestamps.has(formattedTime)) {
        const row: ChartDataPoint = { time: formattedTime };
        for (const choice of nearestSnapshot.predictionChoiceStates) {
          row[choice.choiceId] = Math.round(choice.probability * 100).toString();
        }
        result.push(row);
        usedTimestamps.add(formattedTime);
        // Check if we've included the current time
        if (formattedTime === formatTime(now, true)) {
          currentTimeIncluded = true;
        }
      }
    }

    samplingTime = addMinutes(new Date(samplingTime), intervalMinutes).getTime();
  }

  // Add current time data if not already included
  if (!currentTimeIncluded) {
    const latestSnapshot = history[history.length - 1];
    if (latestSnapshot) {
      const currentFormattedTime = formatTime(now, true);
      const row: ChartDataPoint = { time: currentFormattedTime };
      for (const choice of latestSnapshot.predictionChoiceStates) {
        row[choice.choiceId] = Math.round(choice.probability * 100).toString();
      }
      result.push(row);
    }
  }

  return result;
};

// Helper functions
const formatTime = (timestamp: number, preserveMinutes = false) => {
  let date = new Date(timestamp);
  if (!preserveMinutes) {
    // Round down to the nearest hour (0 minutes)
    date = setMinutes(date, 0);
  }
  date = setSeconds(date, 0);
  date = setMilliseconds(date, 0);
  return format(date, "yyyy/MM/dd HH:mm");
};

// Convert filter type to number of days
const toDays = (filterType: ChartHistoryFilterType): number | null => {
  switch (filterType) {
    case "all":
      return null;
    case "1M":
      return 30;
    case "1W":
      return 7;
    case "1D":
      return 1;
    default:
      return null;
  }
};
